import { can, type Permission } from './access';
import type { Session } from './session';
import { SPACES, type SpaceId } from './spaces';

export interface ViewHome {
  to: `/${string}`;
  permission: Permission;
}

// Routes of each space in menu order; the route table owns the paths.
export type SpaceRouteTable = Readonly<Record<SpaceId, readonly ViewHome[]>>;

type SessionAccess = Pick<Session, 'permissions' | 'roleIds'>;

export function firstPermittedRoute(
  spaceId: SpaceId,
  session: SessionAccess,
  table: SpaceRouteTable,
): ViewHome | null {
  return table[spaceId].find((route) => can(session.permissions, route.permission)) ?? null;
}

// Role held AND a route open by permission; an unknown role id matches no space.
export function usableSpaces(session: SessionAccess, table: SpaceRouteTable): SpaceId[] {
  return SPACES.filter(
    (space) =>
      session.roleIds.includes(space.roleId) && firstPermittedRoute(space.id, session, table) !== null,
  ).map((space) => space.id);
}

// The router matches `/x/` as `/x`; compare paths the same way. Exported for the side menu's current entry.
export function normalizePath(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

export function spaceOfPath(pathname: string, table: SpaceRouteTable): SpaceId | null {
  const path = normalizePath(pathname);
  return SPACES.find((space) => table[space.id].some((route) => route.to === path))?.id ?? null;
}

export function landingFor(session: SessionAccess, table: SpaceRouteTable): ViewHome | null {
  const [first] = usableSpaces(session, table);
  return first === undefined ? null : firstPermittedRoute(first, session, table);
}

// The root opens by permission, so a search extra without the cc role still goes home there.
export function homeFor(root: ViewHome, session: SessionAccess, table: SpaceRouteTable): ViewHome | null {
  return can(session.permissions, root.permission) ? root : landingFor(session, table);
}

// Leading a denied view back to itself would only repeat the denial.
export function reachableHomeHref(
  home: ViewHome | null,
  permissions: readonly string[],
  pathname: string,
): ViewHome['to'] | null {
  if (home === null || !can(permissions, home.permission)) return null;
  return home.to === normalizePath(pathname) ? null : home.to;
}
